// Los datos con los que se busca un producto, traídos recién cuando alguien escribe en un
// buscador (ADR-2026-10-21-la-busqueda-mira-los-datos-del-producto). Las listas de productos no
// los traen: la mayoría de las veces nadie busca, y un catálogo grande suma cientos de KB de
// texto. Se piden sólo los ids de la lista que se está mirando, de a 100 (`fetchInChunks`), y
// quedan en memoria por variante; "Editar producto" olvida la que guardó (`olvidarDelIndice`).
import { useEffect, useMemo, useState } from "react";
import { supabase } from "./supabase";
import { fetchInChunks } from "../utils/supabaseChunkedIn";
import { textoDeBusqueda } from "./busquedaDeProductos";
import {
  caminoDe,
  type CategoriaPlana,
} from "../pages/lanzamientos/arbolDeCategorias";

const cache = new Map<string, string>();
/** El SKU y las categorías de cada variante, para decirle al agente por qué coincidió un producto. */
const resumenes = new Map<string, ResumenParaBuscar>();

export interface ResumenParaBuscar {
  sku: string | null;
  /** Los caminos de sus categorías de la marca ("Remeras › Oversize"). */
  categorias: string[];
}
/** Sube cada vez que el cache cambia, para que los hooks montados se enteren. */
let version = 0;
const oyentes = new Set<() => void>();
const avisar = () => {
  version += 1;
  oyentes.forEach((f) => f());
};

interface Fila {
  id: string;
  product_id: string | null;
  sku: string | null;
  descripcion: string | null;
  descripcion_visual: string | null;
  atributos: unknown;
  productos: { categoria_producto: string | null } | null;
}

const enVuelo = new Set<string>();

/**
 * Los caminos de las categorías de la marca de cada producto ("Remeras › Oversize"). Aparte del
 * pedido principal: si `productos_categorias` todavía no existe (migración 20261021200000) o
 * falla, se busca igual por el resto.
 */
async function categoriasPorProducto(
  productoIds: string[],
): Promise<Map<string, string[]>> {
  const out = new Map<string, string[]>();
  if (productoIds.length === 0) return out;
  try {
    const vinculos = await fetchInChunks<{
      producto_id: string;
      categoria_id: string;
    }>(
      productoIds,
      (chunk) =>
        supabase
          .from("productos_categorias")
          .select("producto_id, categoria_id")
          .in("producto_id", chunk) as unknown as PromiseLike<{
          data: Array<{ producto_id: string; categoria_id: string }> | null;
          error: unknown;
        }>,
    );
    if (vinculos.length === 0) return out;
    // El árbol entero (la RLS lo deja en la empresa): es chico y hace falta para los caminos.
    const { data, error } = await supabase
      .from("categorias_tienda")
      .select("id, nombre, parent_id");
    if (error || !data) return out;
    const arbol: CategoriaPlana[] = (
      data as Array<{ id: string; nombre: string; parent_id: string | null }>
    ).map((c) => ({
      id: c.id,
      nombre: c.nombre,
      parentId: c.parent_id,
    }));
    for (const v of vinculos) {
      const camino = caminoDe(arbol, v.categoria_id);
      if (!camino) continue;
      const lista = out.get(v.producto_id);
      if (lista) lista.push(camino);
      else out.set(v.producto_id, [camino]);
    }
  } catch {
    /* sin categorías: se busca por lo demás */
  }
  return out;
}

async function cargar(ids: string[]): Promise<void> {
  const faltan = ids.filter((id) => !cache.has(id) && !enVuelo.has(id));
  if (faltan.length === 0) return;
  faltan.forEach((id) => enVuelo.add(id));
  try {
    const filas = await fetchInChunks<Fila>(
      faltan,
      (chunk) =>
        supabase
          .from("variantes")
          .select(
            "id, product_id, sku, descripcion, descripcion_visual, atributos, productos(categoria_producto)",
          )
          .in("id", chunk) as unknown as PromiseLike<{
          data: Fila[] | null;
          error: unknown;
        }>,
    );
    const categorias = await categoriasPorProducto([
      ...new Set(
        filas.map((f) => f.product_id).filter((x): x is string => !!x),
      ),
    ]);
    for (const f of filas) {
      resumenes.set(f.id, {
        sku: f.sku?.trim() || null,
        categorias: (f.product_id && categorias.get(f.product_id)) || [],
      });
      cache.set(
        f.id,
        textoDeBusqueda({
          ...f,
          categoria_producto: f.productos?.categoria_producto ?? null,
          categorias: (f.product_id && categorias.get(f.product_id)) || [],
        }),
      );
    }
    // Las que no volvieron (borradas, de otra empresa) quedan vacías: no se vuelven a pedir.
    for (const id of faltan) if (!cache.has(id)) cache.set(id, "");
    avisar();
  } catch (err) {
    // Sin índice la búsqueda sigue por nombre, como antes.
    console.warn(
      "[indiceDeBusqueda] no se pudieron traer los datos de los productos",
      err,
    );
  } finally {
    faltan.forEach((id) => enVuelo.delete(id));
  }
}

/** Renombrar, mover o borrar categorías cambia lo que se busca de muchos productos a la vez. */
export function olvidarTodoElIndice(): void {
  if (cache.size === 0) return;
  cache.clear();
  resumenes.clear();
  avisar();
}

/** Lo guardado en "Editar producto" cambia lo que se busca: la próxima búsqueda lo vuelve a pedir. */
export function olvidarDelIndice(varianteId: string): void {
  resumenes.delete(varianteId);
  if (cache.delete(varianteId)) avisar();
}

/**
 * El índice de estos productos, fuera de React: para la herramienta `buscar_productos` del agente
 * (ADR-2026-10-22-el-agente-busca-productos-por-sku-categoria-y-caracteristicas). Trae lo que falta y
 * devuelve lo que haya; si la lectura falla, un índice vacío (el agente busca sólo por nombre).
 */
export async function indiceParaBuscar(ids: string[]): Promise<{
  indice: ReadonlyMap<string, string>;
  resumen: (id: string) => ResumenParaBuscar | undefined;
}> {
  await cargar(ids);
  // Otro pedido pudo estar trayendo algunos al mismo tiempo: se espera un poco a que terminen.
  for (let i = 0; i < 20 && ids.some((id) => enVuelo.has(id)); i++) {
    await new Promise((r) => setTimeout(r, 250));
  }
  return { indice: new Map(cache), resumen: (id) => resumenes.get(id) };
}

/**
 * El índice de los productos de la lista, mientras `buscando`. Hasta que llega (o si falla),
 * un mapa sin esos ids: `buscarProductos` busca sólo por nombre.
 */
export function useIndiceDeBusqueda(
  ids: string[],
  buscando: boolean,
): ReadonlyMap<string, string> {
  const [v, setV] = useState(version);
  useEffect(() => {
    const oir = () => setV(version);
    oyentes.add(oir);
    return () => {
      oyentes.delete(oir);
    };
  }, []);
  const clave = ids.join(",");
  useEffect(() => {
    if (!buscando || !clave) return;
    void cargar(clave.split(","));
    // `v`: si "Editar producto" olvidó uno mientras se busca, se vuelve a pedir.
  }, [buscando, clave, v]);
  // Un mapa nuevo cuando el cache cambia, para que los `useMemo` de quien filtra se recalculen.
  return useMemo(() => new Map(cache), [v]);
}
